export const SOCIAL_PROVIDERS = [
  "linkedin",
  "instagram",
  "facebook",
  "twitter",
  "tiktok",
  "google",
  "youtube",
  "medium",
  "website",
  "substack"
] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

export const SOCIAL_PROVIDER = {
  LINKEDIN: "linkedin",
  INSTAGRAM: "instagram",
  FACEBOOK: "facebook",
  TWITTER: "twitter",
  TIKTOK: "tiktok",
  GOOGLE: "google",
  YOUTUBE: "youtube",
  MEDIUM: "medium",
  WEBSITE: "website",
  SUBSTACK: "substack",
} as const;

export const CREATOR_STATUSES = [
  "pending_review",
  "approved",
  "rejected",
  "waitlisted",
] as const;
export type CreatorStatus = (typeof CREATOR_STATUSES)[number];

export const CREATOR_STATUS = {
  PENDING_REVIEW: "pending_review",
  APPROVED: "approved",
  REJECTED: "rejected",
  WAITLISTED: "waitlisted",
} as const;

export const OPPORTUNITY_STATUSES = [
  "pending",
  "approved",
  "pending_review",
  "rejected",
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

export const OPPORTUNITY_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  PENDING_REVIEW: "pending_review",
  REJECTED: "rejected",
} as const;

export const CREATOR_BIO_SOURCES = ["ai", "user"] as const;
export type CreatorBioSource = (typeof CREATOR_BIO_SOURCES)[number];

export const CREATOR_THEMES = ["default", "dark", "custom"] as const;
export type CreatorTheme = (typeof CREATOR_THEMES)[number];

export interface SocialProviderConfig {
  name: SocialProvider;
  clientId: string;
  clientSecret: string;
  authorizationUrl: string;
  tokenUrl: string;
  profileUrl: string;
  scopes: string[];
  redirectUri: string;
}

export interface SocialProfile {
  provider: SocialProvider;
  providerUserId: string;
  email?: string;
  emailVerified?: boolean;
  fullName?: string;
  avatarUrl?: string;
  profileUrl?: string;
  handle?: string;
  followersCount?: number;
}

export interface CreatorSocialAccount {
  provider: SocialProvider;
  providerUserId: string;
  profileUrl?: string;
  handle?: string;
  followersCount?: number;
  lastSyncedAt?: Date;
}

export interface CreatorProfileBio {
  generated?: string;
  edited?: string;
  source?: CreatorBioSource;
  lastEditedAt?: Date;
}

export interface CreatorProfileReview {
  lastStatusChangeAt?: Date;
  reviewedBy?: string;
  rejectionReason?: string;
}

export interface CreatorProfile {
  username: string;
  usernameLower: string;
  displayName?: string;
  avatarUrl?: string;
  whatsappNumber?: string;
  isWhatsAppSubscribed?: boolean;
  nicheCategories?: string[];
  skills?: string[];
  primaryPlatform?: SocialProvider;
  bio?: CreatorProfileBio;
  publicLinkUrl?: string;
  status: CreatorStatus;
  review?: CreatorProfileReview;
  isAnonymous?: boolean;
  timezone?: string;
}

export interface CreatorMetrics {
  totalFollowers?: number;
  followersByPlatform?: Partial<Record<SocialProvider, number>>;
}

export interface CreatorAnalytics {
  creatorAchievementCompletedAt?: Date;
  firstJobShareAt?: Date;
  firstPostStudioShareAt?: Date;
  lastJobShareAt?: Date;
  weeklyJobShareCount?: number;
  monthlyJobShareCount?: number;
  jobShareWeekStartAt?: Date;
  jobShareMonthStartAt?: Date;
  reachedMilestones?: number[];
  crFirstJobApplicationSentAt?: Date;
}

export interface CreatorOpportunity {
  id: string;
  title: string;
  category?: string;
  tags?: string[];
  cpa?: number;
  fitScore?: number;
  reason?: string;
}

export interface EarningTier {
  frequency: string;
  weeklyAmount: number;
  monthlyAmount: number;
}

export interface EarningPotential {
  weeklyClicks: number;
  weeklyApplications: number;
  weeklyRevenue: number;
  monthlyEstimate: number;
  tiers: EarningTier[];
}

export interface CreatorJobFeedPreferences {
  positiveKeywords?: string[];
  negativeKeywords?: string[];
  countries?: string[];
  workModes?: string[];
  jobTypes?: string[];
  industries?: string[];
  categories?: string[]; // Add categories (niche categories)
  companies?: string[]; // Company filter for employer tab
  minSalary?: number;
  currency?: string; // Keep for backward compatibility
  currencies?: string[]; // Add multiple currencies
  educationLevel?: string; // Keep for backward compatibility
  educationLevels?: string[]; // Add multiple education levels
  experienceLevels?: string[]; // Add experience levels
  maxSalary?: number;
}

export interface CreatorAppearance {
  theme?: CreatorTheme;
  backgroundColor?: string;  // Page Background
  cardColor?: string;        // Profile Card
  titleColor?: string;       // Title
  ctaColor?: string;         // CTA buttons
  textColor?: string;        // Page Text
}

export interface CreatorSeo {
  metaTitle?: string;
  metaDescription?: string;
  shareCardColor?: string;
  updatedAt?: Date;
}

export interface CreatorReferralMetadata {
  inviteCode?: string;
  inviteCodeCreatedAt?: Date;
}

export const CREATOR_ENRICHMENT_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "failed",
] as const;
export type CreatorEnrichmentStatus = (typeof CREATOR_ENRICHMENT_STATUSES)[number];

export const ENRICHMENT_STEP_STATUSES = [
  "pending",
  "loading",
  "completed",
  "failed",
] as const;
export type EnrichmentStepStatus = (typeof ENRICHMENT_STEP_STATUSES)[number];

export const SOUL_PIPELINE_STATUS = {
  PENDING: "pending",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
  MISSING: "missing",
} as const;

export const SOUL_PIPELINE_STATUSES = [
  SOUL_PIPELINE_STATUS.PENDING,
  SOUL_PIPELINE_STATUS.RUNNING,
  SOUL_PIPELINE_STATUS.COMPLETED,
  SOUL_PIPELINE_STATUS.FAILED,
] as const;
export type SoulPipelineStatus = (typeof SOUL_PIPELINE_STATUSES)[number];

export const SOUL_PIPELINE_STATUSES_WITH_MISSING = [
  SOUL_PIPELINE_STATUS.MISSING,
  ...SOUL_PIPELINE_STATUSES,
] as const;
export type SoulPipelineStatusWithMissing = (typeof SOUL_PIPELINE_STATUSES_WITH_MISSING)[number];

export interface EnrichmentStepTracking {
  readProfile: EnrichmentStepStatus;
  identifyNiche: EnrichmentStepStatus;
  checkOpportunities: EnrichmentStepStatus;
  buildProfile: EnrichmentStepStatus;
}

export interface CreatorEnrichmentData {
  bioGenerated?: string;
  greetingMessage?: string;
  nicheSuggestions?: string[];
  jobKeywords?: string[];
  followersTotal?: number;
  opportunityStatus?: OpportunityStatus;
  decisionReason?: string;
  rejectedAt?: Date;
  opportunities?: CreatorOpportunity[];
  fit?: { approved: boolean; confidence: number };

  // New step tracking fields
  stepStatus?: EnrichmentStepTracking;
  latestSnapshotId?: string;
  profileReadAt?: Date;
  nicheIdentifiedAt?: Date;
  opportunitiesCheckedAt?: Date;
  profileBuiltAt?: Date;

  // Confidence scoring fields
  nicheConfidence?: number;
  confidenceReasoning?: string;
  appealRequestedAt?: Date;

  // Soul pipeline pointers and metadata
  latestSoulSnapshotId?: string;
  soulPipelineStatus?: SoulPipelineStatus;
  soulPipelineVersion?: number;
  soulSourceHash?: string;
  soulGeneratedAt?: Date;
}

export interface CreatorEnrichment {
  status: CreatorEnrichmentStatus;
  lastRunAt?: Date;
  completedAt?: Date;
  failReason?: string;
  data?: CreatorEnrichmentData;
}

export interface CreatorAuth {
  email?: string;
  emailVerified?: boolean;
}

export interface CreatorTlsFingerprints {
  ja3Hashes?: string[];
  ja4Hashes?: string[];
}

export interface Creator {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  auth?: CreatorAuth;
  tlsFingerprints?: CreatorTlsFingerprints;
  socialAccounts?: CreatorSocialAccount[];
  profile: CreatorProfile;
  metrics?: CreatorMetrics;
  jobFeedPreferences?: CreatorJobFeedPreferences;
  appearance?: CreatorAppearance;
  seo?: CreatorSeo;
  referral?: CreatorReferralMetadata;
  enrichment?: CreatorEnrichment;
  analytics?: CreatorAnalytics;
  lastDailyJobNotificationAt?: Date;
  lastWeeklyLeaderboardEmailAt?: Date;
  lastMonthlyLeaderboardEmailAt?: Date;
  lastWeeklyWinnerEmailAt?: Date;
  isFraud?: boolean;
  platformFeeRate?: number;
  hidePoweredBy?: boolean;
}

export interface SocialAuthResult {
  creator: Creator;
  isNewCreator: boolean;
  claimedDraft?: boolean;
}
